import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Button } from '@material-ui/core';

import AppContext from '../../../AppContext';
import TextOutput from '../../../components/form/TextOutput';
import Loader from '../../../components/Loader';
import { UserState } from '../../../reducers/user';
import { Exam } from '../../../types';
import { isValidationErrorResponse } from '../../../utils/common';
import FormWithoutChapters from '../../forms/components/FormWithoutChapters';
import PDFDownload from '../../forms/components/PDFDownload';
import { AnswerValues } from '../../forms/types';
import { determineDynamicFormState } from '../forms/functions';
import ExamFormEntryRepository from '../repository/ExamFormEntryRepository';
import ExamRepository from '../repository/ExamRepository';
import {
  EXAM_FORM_TYPE_TO_EXAM_FIELD_MAPPING,
  ExamFormEntry as TExamFormEntry,
  ExamFormGrade,
  ExamFormGrades,
  ExamFormState,
  ExamFormType,
} from '../types';
import ExamFormEntryGradeSelector from './ExamFormEntryGradeSelector';

interface ExamFormEntryProps {
  exam: Exam;
  type: ExamFormType;
  readOnly: boolean;
  setReadOnly: Dispatch<SetStateAction<boolean>>;
}

const ExamFormEntry = (props: ExamFormEntryProps) => {
  const notifications = useSnackbar();
  const { roleViewManager } = useContext(AppContext);
  const { account } = useSelector((selector: { user: UserState }) => ({
    account: selector.user.account,
  }));

  const [examFormEntry, setExamFormEntry] = useState<
    TExamFormEntry | undefined
  >(undefined);

  const prepareFormEntry = (examId: string, examFormType: ExamFormType) => {
    new ExamRepository()
      .prepareExamFormEntry(examId, examFormType)
      .then((response) => {
        setExamFormEntry(response.data);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er is iets fout gegaan bij het voorbereiden van het rapport.',
          { variant: 'error' },
        );
      });
  };

  const loadFormEntry = (examFormEntryId: number) => {
    new ExamFormEntryRepository()
      .find(examFormEntryId)
      .then((response) => {
        setExamFormEntry(response.data);
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          'Er is iets fout gegaan bij het ophalen van het rapport.',
          { variant: 'error' },
        );
      });
  };

  const dynamicFormState = useMemo(() => {
    return examFormEntry !== undefined
      ? determineDynamicFormState(examFormEntry)
      : undefined;
  }, [examFormEntry]);

  const handleSubmit = useCallback(
    async (answerValues: AnswerValues) => {
      if (examFormEntry === undefined) {
        return;
      }

      const response = await new ExamFormEntryRepository().save(
        examFormEntry.id,
        answerValues,
      );

      if (response.status !== 200) {
        notifications.enqueueSnackbar(
          'Er is iets fout gegaan bij het opslaan van het rapport.',
          { variant: 'error' },
        );

        return;
      }

      if (examFormEntry.state === ExamFormState.OPEN) {
        new ExamFormEntryRepository()
          .submit(examFormEntry.id)
          .then(() => {
            loadFormEntry(examFormEntry.id);

            props.setReadOnly(true);

            notifications.enqueueSnackbar(
              'Het rapport is succesvol opgeslagen en ingediend.',
              { variant: 'success' },
            );
          })
          .catch((e) => {
            if (isValidationErrorResponse(e)) {
              notifications.enqueueSnackbar(
                'Het formulier is opgeslagen, maar kon niet direct ingediend worden. Controleer of alle verplichte velden ingevuld zijn.',
                {
                  variant: 'warning',
                },
              );
            } else {
              notifications.enqueueSnackbar(
                'Er is iets fout gegaan bij het indienen van het rapport.',
                { variant: 'error' },
              );
            }
          });
      } else {
        loadFormEntry(examFormEntry.id);

        props.setReadOnly(true);

        notifications.enqueueSnackbar('Het rapport is succesvol opgeslagen.', {
          variant: 'success',
        });
      }
    },
    [examFormEntry],
  );

  const handleGradeSubmit = useCallback(
    async (grade: ExamFormGrade) => {
      if (examFormEntry === undefined) {
        return;
      }

      new ExamFormEntryRepository()
        .grade(examFormEntry.id, grade)
        .then(() => {
          // refresh
          loadFormEntry(examFormEntry.id);

          notifications.enqueueSnackbar(
            'Het rapport is succesvol beoordeeld.',
            { variant: 'success' },
          );
        })
        .catch(() => {
          notifications.enqueueSnackbar(
            'Er is iets fout gegaan bij het beoordelen van het rapport.',
            { variant: 'error' },
          );
        });
    },
    [examFormEntry],
  );

  useEffect(() => {
    const examFormEntry =
      props.exam[EXAM_FORM_TYPE_TO_EXAM_FIELD_MAPPING[props.type]];

    if (examFormEntry !== undefined) {
      loadFormEntry(examFormEntry.id);
    } else {
      prepareFormEntry(props.exam.id, props.type);
    }
  }, [props.type, props.exam]);

  if (!examFormEntry || !dynamicFormState) {
    return <Loader inline />;
  }

  return (
    <>
      {examFormEntry.formEntry.pdf !== undefined ? (
        <PDFDownload pdf={examFormEntry.formEntry.pdf} />
      ) : (
        <div>
          <FormWithoutChapters
            formEntry={examFormEntry.formEntry}
            dynamicFormState={dynamicFormState}
            onSubmit={handleSubmit}
            readOnly={props.readOnly}
          />
          {(props.exam.closedAt
            ? roleViewManager.isSuperAdminView()
            : roleViewManager.isAdminView()) &&
            props.readOnly && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => props.setReadOnly(false)}
              >
                Verslag bewerken
              </Button>
            )}
        </div>
      )}
      {roleViewManager.isAdminView() ||
      account?.delegateProfile?.status === 'mentor' ? (
        <>
          {examFormEntry.state === ExamFormState.IN_REVIEW ? (
            <ExamFormEntryGradeSelector
              examFormEntryType={props.type}
              onSubmit={handleGradeSubmit}
            />
          ) : null}
          {examFormEntry.state === ExamFormState.FINALIZED &&
          examFormEntry.grade !== null ? (
            <TextOutput
              label="Beoordeling"
              value={ExamFormGrades[examFormEntry.grade]}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default ExamFormEntry;
